'use client';

import { useEffect, useState, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Notification {
  id: string;
  type: 'price_drop' | 'booking_confirmed' | 'flight_delay' | 'deal_alert' | 'price_update';
  title: string;
  message: string;
  timestamp: Date;
}

const RealtimeNotificationToast = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    // Simulate real-time notifications (in production, this would be WebSocket or Server-Sent Events)
    const notificationTypes = [
      {
        type: 'price_drop' as const,
        titles: ['Price Drop Alert!', 'Great Deal Found!', 'Price Reduced!'],
        messages: [
          'London to Dubai flight dropped to £899',
          'Maldives holiday package now £2,599',
          'New York hotel reduced by 25%',
          'Paris flights £50 cheaper',
        ],
      },
      {
        type: 'price_update' as const,
        titles: ['Live Price Update', 'Price Changed', 'New Price Available'],
        messages: [
          'Dubai to London: £1,249 → £1,189',
          'Maldives Package: £2,899 → £2,799',
          'Swiss Alps Hotel: £329 → £299',
          'Caribbean Resort: £1,899 → £1,849',
        ],
      },
      {
        type: 'deal_alert' as const,
        titles: ['Flash Deal!', 'Limited Time Offer!', 'Hot Deal Alert!'],
        messages: [
          'Last 3 seats to Barcelona at £149',
          '24-hour flash sale: 30% off all hotels',
          'Weekend getaway packages from £299',
          'Business class upgrade 50% off',
        ],
      },
      {
        type: 'booking_confirmed' as const,
        titles: ['Booking Confirmed', 'Reservation Complete', 'PNR Generated'],
        messages: [
          'Your flight to Dubai is confirmed - PNR: ABC123',
          'Hotel booking successful - Confirmation: HTL456',
          'Holiday package booked - Reference: PKG789',
        ],
      },
      {
        type: 'flight_delay' as const,
        titles: ['Flight Update', 'Schedule Change', 'Status Alert'],
        messages: [
          'EK203 delayed by 30 minutes',
          'Gate change: Flight BA107 now at Gate 15',
          'Boarding started for QR009',
        ],
      },
    ];

    const generateNotification = () => {
      const randomType = notificationTypes[Math.floor(Math.random() * notificationTypes.length)];
      const randomTitle = randomType.titles[Math.floor(Math.random() * randomType.titles.length)];
      const randomMessage = randomType.messages[Math.floor(Math.random() * randomType.messages.length)];

      const newNotification: Notification = {
        id: `notif-${Date.now()}-${Math.random()}`,
        type: randomType.type,
        title: randomTitle,
        message: randomMessage,
        timestamp: new Date(),
      };

      setNotifications((prev) => [newNotification, ...prev].slice(0, 3));

      // Auto-dismiss after 5 seconds - track timeout for cleanup
      const dismissTimeout = setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== newNotification.id));
      }, 5000);
      timeoutsRef.current.push(dismissTimeout);
    };

    // Generate notifications every 8-15 seconds (use fixed interval to avoid hydration issues)
    const interval = setInterval(() => {
      generateNotification();
    }, 10000);

    // Generate first notification after 3 seconds - track timeout for cleanup
    const initialTimeout = setTimeout(generateNotification, 3000);
    timeoutsRef.current.push(initialTimeout);

    return () => {
      clearInterval(interval);
      // Clear all tracked timeouts on unmount
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'price_drop': case'price_update':
        return { name: 'ArrowTrendingDownIcon' as const, color: 'text-success' };
      case 'booking_confirmed':
        return { name: 'CheckCircleIcon' as const, color: 'text-success' };
      case 'flight_delay':
        return { name: 'ClockIcon' as const, color: 'text-warning' };
      case 'deal_alert':
        return { name: 'BellAlertIcon' as const, color: 'text-coral' };
      default:
        return { name: 'BellIcon' as const, color: 'text-primary' };
    }
  };

  const getNotificationBg = (type: Notification['type']) => {
    switch (type) {
      case 'price_drop': case'price_update':
        return 'bg-success/10 border-success/30';
      case 'booking_confirmed':
        return 'bg-success/10 border-success/30';
      case 'flight_delay':
        return 'bg-warning/10 border-warning/30';
      case 'deal_alert':
        return 'bg-coral/10 border-coral/30';
      default:
        return 'bg-primary/10 border-primary/30';
    }
  };

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-50 space-y-3 max-w-sm">
      {notifications.map((notification) => {
        const iconConfig = getNotificationIcon(notification.type);
        return (
          <div
            key={notification.id}
            className={`${getNotificationBg(notification.type)} border-2 rounded-lg shadow-brand-lg p-4 backdrop-blur-sm animate-slide-in-right`}
          >
            <div className="flex items-start space-x-3">
              <div className={`${iconConfig.color} mt-0.5`}>
                <Icon name={iconConfig.name} size={20} variant="solid" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-heading font-bold text-sm text-foreground mb-1">
                  {notification.title}
                </h4>
                <p className="text-sm text-muted-foreground">{notification.message}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {notification.timestamp.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
              <button
                onClick={() =>
                  setNotifications((prev) => prev.filter((n) => n.id !== notification.id))
                }
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="XMarkIcon" size={16} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RealtimeNotificationToast;